import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, Container, Stack, Alert, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

const LoginPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const { data } = await axios.post(getApiUrl('auth/login'), { email, password });

            // Save token and user info
            localStorage.setItem('userInfo', JSON.stringify(data));

            // Redirect based on role
            if (data.role === 'PARENT') {
                navigate('/dashboard/parent-portal');
            } else if (data.role === 'RECEPTIONIST') {
                navigate('/dashboard/receptionist');
            } else if (data.role === 'SAAS_ADMIN') {
                navigate('/dashboard/saas-admin');
            } else {
                navigate('/dashboard');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#0B0F19',
                backgroundImage: 'radial-gradient(circle at 50% -20%, #1a237e 0%, #0B0F19 70%)'
            }}
        >
            <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate('/')}
                    sx={{ color: 'white', textTransform: 'none', fontWeight: 600 }}
                >
                    Back to Website
                </Button>
            </Box>
            <Container maxWidth="sm">
                <Paper
                    elevation={10}
                    sx={{
                        p: 5,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}
                >
                    <Stack spacing={3} alignItems="center">
                        <Box sx={{ width: 50, height: 50, bgcolor: '#ffab00', borderRadius: 2 }} />
                        <Typography variant="h4" fontWeight="bold" color="white">Welcome Back</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.6)">Sign in to access the SquareEdge Portal</Typography>

                        {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}

                        <form onSubmit={handleLogin} style={{ width: '100%' }}>
                            <Stack spacing={3}>
                                <TextField
                                    label="Email Address"
                                    variant="outlined"
                                    fullWidth
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                    InputProps={{
                                        style: { color: 'white' },
                                        sx: { bgcolor: 'rgba(255,255,255,0.05)' }
                                    }}
                                />
                                <TextField
                                    label="Password"
                                    type="password"
                                    variant="outlined"
                                    fullWidth
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                    InputProps={{
                                        style: { color: 'white' },
                                        sx: { bgcolor: 'rgba(255,255,255,0.05)' }
                                    }}
                                />
                                
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Link 
                                        to="/forgot-password" 
                                        style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '0.875rem' }}
                                    >
                                        Forgot Password?
                                    </Link>
                                </Box>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    disabled={loading}
                                    sx={{
                                        bgcolor: '#ffab00',
                                        color: 'black',
                                        fontWeight: 'bold',
                                        py: 1.5,
                                        '&:hover': { bgcolor: '#ffca28' }
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Sign In'}
                                </Button>
                            </Stack>
                        </form>

                        <Typography variant="body2" color="rgba(255,255,255,0.6)">
                            Don't have an account?{' '}
                            <Link to="/register" style={{ color: '#ffab00', textDecoration: 'none', fontWeight: 'bold' }}>
                                Register as Parent
                            </Link>
                        </Typography>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default LoginPage;
